/* GOWin® Store — hardening del carrito v1: concurrencia, reglas, scroll y accesibilidad */
(() => {
  'use strict';
  if (window.GowinCartHardeningV1) return;
  window.GowinCartHardeningV1 = true;

  const CUSTOM_INPUT = '.gowin-cart-qty-inline__custom';
  const REAL_INPUT = '.cart-item__quantity .quantity__input';
  const SUGGESTION_SOURCE = 'gowin-drawer-suggestion-v3';
  let trackedScroll = { inner: 0, items: 0, cartItems: 0, recommendations: 0 };
  let observer;

  const int = (value, fallback = null) => {
    const parsed = parseInt(value ?? '', 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const rulesFor = (input) => ({
    min: Math.max(1, int(input?.dataset?.min, 1)),
    max: int(input?.getAttribute('max')),
    step: Math.max(1, int(input?.getAttribute('step'), 1)),
  });

  const validQuantity = (input, quantity) => {
    const qty = int(quantity);
    if (!Number.isInteger(qty) || qty <= 0) return false;
    const { min, max, step } = rulesFor(input);
    return qty >= min && (max === null || qty <= max) && (qty - min) % step === 0;
  };

  const validationMessage = (input, quantity) => {
    const qty = int(quantity);
    const { min, max, step } = rulesFor(input);
    const strings = window.quickOrderListStrings || {};
    if (!Number.isInteger(qty) || qty < min) return strings.min_error?.replace('[min]', min) || `La cantidad mínima es ${min}.`;
    if (max !== null && qty > max) return strings.max_error?.replace('[max]', max) || `La cantidad máxima es ${max}.`;
    if ((qty - min) % step !== 0) return strings.step_error?.replace('[step]', step) || `La cantidad debe aumentar en incrementos de ${step} desde ${min}.`;
    return '';
  };

  const nextValidAtOrAbove = (input, floor) => {
    const { min, step } = rulesFor(input);
    if (floor <= min) return min;
    return min + Math.ceil((floor - min) / step) * step;
  };

  const rowFor = (input) => input?.closest?.('.cart-item');

  const getErrorParts = (row) => {
    if (!row) return {};
    const details = row.querySelector('.cart-item__details');
    const error = row.querySelector('.cart-item__error');
    const text = error?.querySelector('.cart-item__error-text');
    return { details, error, text };
  };

  const prepareError = (row) => {
    const { details, error, text } = getErrorParts(row);
    if (!details || !error || !text) return;
    if (error.parentElement !== details) details.appendChild(error);
    error.classList.add('gowin-cart-item-error-visible');

    const active = error.dataset.gowinErrorActive === 'true' && Boolean((text.textContent || '').trim());
    if (!active) {
      text.textContent = '';
      error.dataset.gowinErrorActive = 'false';
    }
    error.classList.toggle('is-visible', active);
    error.setAttribute('aria-hidden', active ? 'false' : 'true');
  };

  const setError = (row, message = '') => {
    const { details, error, text } = getErrorParts(row);
    if (!details || !error || !text) return;
    if (error.parentElement !== details) details.appendChild(error);
    error.classList.add('gowin-cart-item-error-visible');

    const value = String(message || '').trim();
    const active = Boolean(value);
    text.textContent = value;
    error.dataset.gowinErrorActive = active ? 'true' : 'false';
    error.classList.toggle('is-visible', active);
    error.setAttribute('aria-hidden', active ? 'false' : 'true');
  };

  const stateErrorMessage = (errors) => {
    if (!errors) return '';
    if (typeof errors === 'string') return errors;
    if (Array.isArray(errors)) {
      return errors.map((item) => stateErrorMessage(item)).filter(Boolean).join(' ');
    }
    if (typeof errors === 'object') {
      return errors.description || errors.message || errors.error || '';
    }
    return String(errors);
  };

  const recoverableAvailabilityQuantity = (errors, requestedQuantity) => {
    const requested = int(requestedQuantity);
    if (!Number.isInteger(requested) || requested <= 1) return null;

    const message = stateErrorMessage(errors).replace(/\s+/g, ' ').trim();
    if (!message) return null;

    const patterns = [
      /\bsolo\s+se\s+(?:añadieron|agregaron)\s+(\d+)\s+(?:artículos|articulos|productos|unidades)\b/i,
      /\bsolo\s+(?:puedes|puede)\s+(?:añadir|agregar|comprar|tener)\s+(\d+)\b/i,
      /\bonly\s+(\d+)\s+(?:items?|products?|units?)\s+(?:were\s+)?added\b/i,
      /\b(?:you\s+can\s+)?only\s+(?:add|have|buy)\s+(\d+)\b/i,
      /\b(\d+)\s+(?:items?|products?|units?|artículos|articulos|productos|unidades)\s+(?:are\s+)?(?:available|disponibles?)\b/i,
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      const candidate = int(match?.[1]);
      if (Number.isInteger(candidate) && candidate > 0 && candidate < requested) return candidate;
    }

    return null;
  };

  const sanitizeRow = (row) => {
    const input = row.querySelector(REAL_INPUT);
    const select = row.querySelector('select.gowin-cart-qty-inline__select');
    if (!input) return;

    prepareError(row);

    if (select) {
      [...select.querySelectorAll('option')].forEach((option) => {
        const qty = int(option.value);
        if (qty !== null && qty > 0 && !validQuantity(input, qty) && Number(option.value) !== Number(input.value)) option.remove();
      });
    }

    const custom = row.querySelector(CUSTOM_INPUT);
    if (custom) {
      const { min, max, step } = rulesFor(input);
      custom.min = String(min);
      custom.step = String(step);
      if (max !== null) custom.max = String(max);
      else custom.removeAttribute('max');
      const current = int(custom.value);
      if (!validQuantity(input, current)) {
        const next = nextValidAtOrAbove(input, 11);
        if (max === null || next <= max) custom.value = String(next);
      }
    }
  };

  const sanitize = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.querySelectorAll('.cart-items .cart-item').forEach(sanitizeRow);
  };

  const captureScroll = (drawer = document.querySelector('cart-drawer')) => {
    if (!drawer) return { ...trackedScroll };
    const inner = drawer.querySelector('.drawer__inner');
    const items = drawer.querySelector('cart-drawer-items');
    const cartItems = drawer.querySelector('#CartDrawer-CartItems');
    const recommendations = drawer.querySelector('.gowin-drawer-shipping__viewport');
    return {
      inner: inner?.scrollTop || 0,
      items: items?.scrollTop || 0,
      cartItems: cartItems?.scrollTop || 0,
      recommendations: recommendations?.scrollLeft || 0,
    };
  };

  const restoreScroll = (drawer, state) => {
    if (!drawer || !state) return;
    const inner = drawer.querySelector('.drawer__inner');
    const items = drawer.querySelector('cart-drawer-items');
    const cartItems = drawer.querySelector('#CartDrawer-CartItems');
    const recommendations = drawer.querySelector('.gowin-drawer-shipping__viewport');
    if (inner) inner.scrollTop = state.inner;
    if (items) items.scrollTop = state.items;
    if (cartItems) cartItems.scrollTop = state.cartItems;
    if (recommendations) recommendations.scrollLeft = state.recommendations;
  };

  const refreshEmptyFocus = (drawer) => {
    if (!drawer?.classList.contains('is-empty') || typeof window.trapFocus !== 'function') return;
    requestAnimationFrame(() => {
      const container = drawer.querySelector('.drawer__inner');
      const target = drawer.querySelector('.drawer__header .drawer__close') || drawer.querySelector('.cart-drawer__empty-content a[href]') || container;
      if (container && target) window.trapFocus(container, target);
    });
  };

  const installQuantityGuard = () => {
    const CartDrawerItems = customElements.get('cart-drawer-items');
    const prototype = CartDrawerItems?.prototype;
    if (!prototype || typeof prototype.updateQuantity !== 'function') return false;
    if (prototype.updateQuantity.GowinHardeningV1) return true;

    const wrapped = prototype.updateQuantity;
    const hardened = function (line, quantity, event, name, variantId) {
      const drawer = this.closest('cart-drawer');
      if (!drawer) return wrapped.call(this, line, quantity, event, name, variantId);

      const input = drawer.querySelector(`${REAL_INPUT}[data-index="${line}"]`) || document.getElementById(`Drawer-quantity-${line}`);
      const qty = int(quantity, 0);
      if (qty > 0 && input && !validQuantity(input, qty)) {
        const message = validationMessage(input, qty);
        input.value = input.getAttribute('value') || input.value;
        setError(rowFor(input), message);
        document.dispatchEvent(new CustomEvent('gowin:cart-patched', { detail: { line, quantity: qty, error: true, clientValidation: true } }));
        return Promise.resolve({ errors: message });
      }

      if (input) setError(rowFor(input), '');

      const scrollState = captureScroll(drawer);

      const finalizeState = (state, preferredMessage = '') => {
        restoreScroll(drawer, scrollState);
        requestAnimationFrame(() => restoreScroll(drawer, scrollState));

        const currentRow = drawer.querySelector(`#CartDrawer-Item-${line}`);
        const serverMessage = stateErrorMessage(state?.errors);
        const actualQuantity = int(state?.items?.[line - 1]?.quantity);
        const adjustedMessage = !serverMessage && qty > 0 && actualQuantity !== null && actualQuantity !== qty
          ? (window.cartStrings?.quantityError?.replace('[quantity]', actualQuantity) || `La cantidad disponible se ajustó a ${actualQuantity}.`)
          : '';
        const message = preferredMessage || serverMessage || adjustedMessage;
        setError(currentRow, message);
        if (message && typeof this.updateLiveRegions === 'function') this.updateLiveRegions(line, message);

        refreshEmptyFocus(drawer);
        sanitize();
        return state;
      };

      const run = () => Promise.resolve(wrapped.call(this, line, quantity, event, name, variantId)).then((state) => {
        const initialMessage = stateErrorMessage(state?.errors);
        const recoveryQuantity = recoverableAvailabilityQuantity(state?.errors, qty);

        if (!state?.errors || recoveryQuantity === null || !input || !validQuantity(input, recoveryQuantity)) {
          return finalizeState(state);
        }

        return Promise.resolve(wrapped.call(this, line, recoveryQuantity, event, name, variantId)).then((recoveredState) => {
          const recoveryError = stateErrorMessage(recoveredState?.errors);
          if (recoveryError) return finalizeState(recoveredState);

          const actualQuantity = int(recoveredState?.items?.[line - 1]?.quantity, recoveryQuantity);
          const message = actualQuantity !== recoveryQuantity
            ? (window.cartStrings?.quantityError?.replace('[quantity]', actualQuantity) || `La cantidad disponible se ajustó a ${actualQuantity}.`)
            : initialMessage;

          return finalizeState(recoveredState, message);
        });
      });

      const previous = this._gowinHardeningQueue || Promise.resolve();
      const task = previous.catch(() => undefined).then(run);
      this._gowinHardeningQueue = task;
      return task;
    };

    hardened.GowinHardeningV1 = true;
    hardened.wrappedUpdateQuantity = wrapped;
    prototype.updateQuantity = hardened;
    return true;
  };

  const bindCustomValidation = () => {
    document.addEventListener('change', (event) => {
      const custom = event.target;
      if (!(custom instanceof Element) || !custom.matches(CUSTOM_INPUT)) return;
      const row = custom.closest('.cart-item');
      const input = row?.querySelector(REAL_INPUT);
      if (!input) return;
      const qty = int(custom.value);
      const message = validationMessage(input, qty);
      custom.setCustomValidity(message);
      if (!message) {
        setError(row, '');
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      setError(row, message);
      custom.reportValidity();
    }, true);

    document.addEventListener('input', (event) => {
      const custom = event.target;
      if (!(custom instanceof Element) || !custom.matches(CUSTOM_INPUT)) return;
      custom.setCustomValidity('');
      setError(custom.closest('.cart-item'), '');
    }, true);
  };

  const bindScrollTracking = () => {
    trackedScroll = captureScroll();
    document.addEventListener('scroll', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !(event.target instanceof Element) || !drawer.contains(event.target)) return;
      trackedScroll = captureScroll(drawer);
    }, true);

    document.addEventListener('cart:updated', (event) => {
      if (event.detail?.source !== SUGGESTION_SOURCE) return;
      const drawer = document.querySelector('cart-drawer');
      const saved = { ...trackedScroll };
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        restoreScroll(drawer, saved);
        trackedScroll = { ...saved };
        sanitize();
      })));
    });
  };

  const bindObserver = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    observer?.disconnect();
    observer = new MutationObserver(() => requestAnimationFrame(sanitize));
    observer.observe(drawer, { childList: true, subtree: true, characterData: true });
    sanitize();
  };

  const install = () => {
    installQuantityGuard();
    bindObserver();
  };

  bindCustomValidation();
  bindScrollTracking();
  install();
  if (!customElements.get('cart-drawer-items')) customElements.whenDefined('cart-drawer-items').then(installQuantityGuard);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', install, { once: true }) : install();
  document.addEventListener('shopify:section:load', install);
  document.addEventListener('gowin:cart-patched', sanitize);
  window.addEventListener('pageshow', sanitize);
})();
